import React, { useEffect } from 'react';
import { generateMetaphors, generateQuiz, generateLearningPath } from '../api/sonarAPI';
import LoadingSpinner from './LoadingSpinner';
import Quiz from './Quiz';

function FeatureSection({
  topic,
  complexityLevel,
  loading,
  setLoading,
  featureOutput,
  setFeatureOutput,
  activeFeature,
  setActiveFeature,
  apiKey
}) {
  const handleFeatureClick = async (feature) => {
    if (!topic.trim()) return;
    if (!apiKey.trim()) {
        setFeatureOutput("Please enter your Sonar API key in the input section above.");
        return;
      }
    
    setActiveFeature(feature);
    setLoading(true);
    
    try {
      let result;
      switch (feature) {
        case 'metaphor':
          result = await generateMetaphors(topic, apiKey);
          break;
        case 'quiz':
          result = await generateQuiz(topic, complexityLevel, apiKey);
          break;
        case 'learningPath':
          result = await generateLearningPath(topic, complexityLevel, apiKey);
          break;
        default:
          result = null;
      }
      setFeatureOutput(result);
    } catch (error) {
      setFeatureOutput("Sorry, there was an error. Please check your API key and try again.");
    } finally {
      setLoading(false);
    }
  };

  // Expose handleFeatureClick to window for InputSection to use
  useEffect(() => {
    window.handleFeatureClick = handleFeatureClick;
    window.activeFeature = activeFeature;
    
    return () => {
      delete window.handleFeatureClick;
      delete window.activeFeature;
    };
  }, [activeFeature, topic, apiKey, complexityLevel]);

  const renderFeatureOutput = () => {
    if (loading) return <LoadingSpinner message="Generating content... It takes a few minutes..." />;
    
    if (!featureOutput) return <p className="placeholder">Additional content will appear here...</p>;
    
    switch (activeFeature) {
      case 'metaphor':
        return (
          <div className="feature-metaphor">
            <h4>Metaphors</h4>
            {featureOutput.map((metaphor, index) => (
              <div key={index} className="metaphor-card">
                <h5>{metaphor.title}</h5>
                <p>{metaphor.description}</p>
              </div>
            ))}
          </div>
        );
      
      case 'quiz':
        return <Quiz questions={featureOutput} />;
      
      case 'learningPath':
        return (
          <div className="learning-path">
            <h4>Learning Path</h4>
            {featureOutput.map((step) => (
              <div key={step.step} className="path-step">
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h5>{step.title}</h5>
                  <p>{step.description}</p>
                  {step.resources && (
                    <div className="resources">
                      <p>Resources:</p>
                      <ul>
                        {Array.isArray(step.resources) ? (
                          step.resources.map((resource, index) => (
                            <li key={index}>
                              <a href={resource.link} target="_blank" rel="noopener noreferrer">
                                {resource.name}
                              </a>
                            </li>
                          ))
                        ) : (
                          <li>{step.resources}</li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      
      default:
        return <p>Select a feature to generate content.</p>;
    }
  };

  return (
    <div className="features-section">
      <h3>Additional Tools:</h3>
      <div id="feature-output" className="feature-result">
        {renderFeatureOutput()}
      </div>
    </div>
  );
}

export default FeatureSection;